import React, { useState, useContext, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";

import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import { alpha } from "@mui/material/styles";
import { useTheme } from "@mui/material/styles";
import DescriptionIcon from "@mui/icons-material/Description";

import {
    useService as useAudioDocumentService,
    AudioDocumentStatus,
    AudioDocument,
} from "../../services/AudioDocuments";
import { Context as AuthContext } from "../../context/Auth";

function getStatusColor(
    status: AudioDocumentStatus,
): "default" | "primary" | "success" | "error" {
    switch (status) {
        case AudioDocumentStatus.READY:
            return "success";
        case AudioDocumentStatus.PROCESSING:
            return "primary";
        case AudioDocumentStatus.FAILED:
            return "error";
        default:
            return "default";
    }
}

export default function DashboardLibrary() {
    const audioService = useAudioDocumentService();
    const { state: authState } = useContext(AuthContext);
    const navigate = useNavigate();
    const theme = useTheme();

    const isAuthenticated = Boolean(authState.user);
    const [documentsLoading, setDocumentsLoading] = useState(true);
    const [documents, setDocuments] = useState<AudioDocument[]>([]);

    const fetchDocuments = useCallback(async () => {
        if (!isAuthenticated) {
            setDocumentsLoading(false);
            setDocuments([]);
            return;
        }

        try {
            setDocumentsLoading(true);
            const response = await audioService.list({ limit: 100 });
            // Sort by most recent first (createdAt descending)
            const sorted = [...response.data].sort((a, b) => {
                return (
                    new Date(b.createdAt).getTime() -
                    new Date(a.createdAt).getTime()
                );
            });
            setDocuments(sorted);
        } catch (err) {
            console.error("Failed to fetch audio documents:", err);
        } finally {
            setDocumentsLoading(false);
        }
    }, [audioService, isAuthenticated]);

    useEffect(() => {
        fetchDocuments();
    }, [fetchDocuments]);

    const handleCardClick = (docId: string) => {
        navigate(`/dashboard/library/${docId}`);
    };

    // Get preview text (first 2-3 lines, roughly 150 characters)
    const getPreviewText = (doc: AudioDocument): string => {
        const text = doc.description || doc.text;
        // Get first ~150 characters and truncate at word boundary
        const preview = text.slice(0, 150);
        const lastSpace = preview.lastIndexOf(" ");
        return lastSpace > 100
            ? preview.slice(0, lastSpace) + "..."
            : preview + "...";
    };

    // Format relative time (e.g., "2 hours ago", "3 hours ago")
    const formatRelativeTime = (date: Date): string => {
        const now = new Date();
        const diffInMs = now.getTime() - date.getTime();
        const diffInSeconds = Math.floor(diffInMs / 1000);
        const diffInMinutes = Math.floor(diffInSeconds / 60);
        const diffInHours = Math.floor(diffInMinutes / 60);
        const diffInDays = Math.floor(diffInHours / 24);

        if (diffInDays > 0) {
            return `${diffInDays} day${diffInDays > 1 ? "s" : ""} ago`;
        } else if (diffInHours > 0) {
            return `${diffInHours} hour${diffInHours > 1 ? "s" : ""} ago`;
        } else if (diffInMinutes > 0) {
            return `${diffInMinutes} minute${diffInMinutes > 1 ? "s" : ""} ago`;
        } else {
            return "just now";
        }
    };

    return (
        <Container
            disableGutters
            sx={{
                width: "100%",
                mx: "auto",
                maxWidth: {
                    xs: "100%",
                    sm: 720,
                    md: 1200,
                },
                mt: { xs: 1, sm: 2 },
                pb: { xs: 2, sm: 3 },
            }}
        >
            <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
                    Your Files
                </Typography>

                {documentsLoading ? (
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            py: 8,
                        }}
                    >
                        <CircularProgress />
                    </Box>
                ) : documents.length === 0 ? (
                    <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{ mt: 4, textAlign: "center" }}
                    >
                        No audio documents yet. Create one in the "Create" tab
                        to get started!
                    </Typography>
                ) : (
                    <Grid container spacing={3}>
                        {documents.map((doc) => (
                            <Grid
                                item
                                xs={12}
                                sm={6}
                                md={4}
                                lg={3}
                                key={doc.id}
                            >
                                <Card
                                    elevation={0}
                                    sx={{
                                        borderRadius: 2,
                                        border: "1px solid",
                                        borderColor: alpha(
                                            theme.palette.common.white,
                                            0.1,
                                        ),
                                        backgroundColor:
                                            theme.palette.background.paper,
                                        cursor: "pointer",
                                        transition: "all 0.2s ease-in-out",
                                        height: "100%",
                                        display: "flex",
                                        flexDirection: "column",
                                        overflow: "hidden",
                                        "&:hover": {
                                            borderColor:
                                                theme.palette.primary.main,
                                            boxShadow: `0 8px 16px ${alpha(theme.palette.primary.main, 0.2)}`,
                                            transform: "translateY(-2px)",
                                        },
                                    }}
                                    onClick={() => handleCardClick(doc.id)}
                                >
                                    <CardContent
                                        sx={{
                                            p: 2,
                                            display: "flex",
                                            flexDirection: "column",
                                            height: "100%",
                                            position: "relative",
                                            pb: 1.5,
                                        }}
                                    >
                                        {/* Icon/Thumbnail area */}
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                mb: 2,
                                                height: 80,
                                                backgroundColor: alpha(
                                                    theme.palette.primary.main,
                                                    0.1,
                                                ),
                                                borderRadius: 1,
                                            }}
                                        >
                                            <DescriptionIcon
                                                sx={{
                                                    fontSize: 48,
                                                    color: theme.palette.primary
                                                        .main,
                                                }}
                                            />
                                        </Box>

                                        {/* Status pill at top right */}
                                        <Box
                                            sx={{
                                                position: "absolute",
                                                top: 12,
                                                right: 12,
                                            }}
                                        >
                                            <Chip
                                                label={doc.status}
                                                color={getStatusColor(
                                                    doc.status,
                                                )}
                                                size="small"
                                                sx={{
                                                    fontSize: "0.65rem",
                                                    height: 18,
                                                }}
                                            />
                                        </Box>

                                        {/* Title/Description */}
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                color: "text.primary",
                                                fontWeight: 500,
                                                mb: 0.5,
                                                display: "-webkit-box",
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: "vertical",
                                                overflow: "hidden",
                                                minHeight: "2.5em",
                                            }}
                                        >
                                            {getPreviewText(doc)}
                                        </Typography>

                                        {/* Time ago */}
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: alpha(
                                                    theme.palette.text
                                                        .secondary,
                                                    0.7,
                                                ),
                                                fontSize: "0.75rem",
                                            }}
                                        >
                                            {formatRelativeTime(
                                                new Date(doc.createdAt),
                                            )}{" "}
                                            • txt
                                        </Typography>
                                    </CardContent>

                                    {/* Bottom border accent */}
                                    <Box
                                        sx={{
                                            height: 3,
                                            backgroundColor:
                                                theme.palette.primary.main,
                                            width: "100%",
                                        }}
                                    />
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                )}
            </Box>
        </Container>
    );
}
